import { Events } from './events';
import { computeProjectedPolygonArea } from './measurement-geometry';
import { withProvenance } from './provenance';

type MeasurementKind = 'distance' | 'polyline' | 'area' | 'angle';

type Measurement = {
    id: string;
    kind: MeasurementKind;
    name?: string;
    description?: string;
    points: [number, number, number][];
    units?: 'm' | 'ft';
    layerId?: string;
    author?: string;
    createdAt?: string;

    // Legacy compatibility fields consumed by existing measurement list UI.
    number?: number;
    a?: number[];
    b?: number[];
    length?: number;
    area?: number;
    visible?: boolean;
    visibilityTouched?: boolean;
};

type MeasurementUpdatePayload = {
    id: string;
    patch: Partial<Measurement>;
};

type LegacyMeasurementAddPayload = {
    a: number[];
    b: number[];
    length: number;
};

type MeasurementRestorePayload = {
    items?: unknown;
    selected?: string | number | null;
    nextNumber?: number;
};

type CsvColumn =
    | 'id'
    | 'number'
    | 'name'
    | 'type'
    | 'value'
    | 'units'
    | 'description'
    | 'pointCount'
    | 'points'
    | 'createdAt'
    | 'author'
    | 'layerId';

const SQFT_PER_SQM = 10.763910416709722;

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const sanitizePoint = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) {
        return null;
    }

    const [x, y, z] = value;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

const sanitizePoints = (value: unknown): [number, number, number][] | null => {
    if (!Array.isArray(value)) {
        return null;
    }

    const result: [number, number, number][] = [];
    for (const item of value) {
        const point = sanitizePoint(item);
        if (!point) {
            return null;
        }
        result.push(point);
    }

    return result.length >= 2 ? result : null;
};

const sanitizeKind = (value: unknown): MeasurementKind | null => {
    return value === 'distance' || value === 'polyline' || value === 'area' || value === 'angle' ? value : null;
};

const sanitizeUnits = (value: unknown): 'm' | 'ft' | undefined => {
    return value === 'm' || value === 'ft' ? value : undefined;
};

const cloneMeasurement = (measurement: Measurement): Measurement => {
    return {
        ...measurement,
        points: measurement.points.map(point => [point[0], point[1], point[2]]),
        a: measurement.a ? [measurement.a[0], measurement.a[1], measurement.a[2]] : undefined,
        b: measurement.b ? [measurement.b[0], measurement.b[1], measurement.b[2]] : undefined
    };
};

const computeDistance = (measurement: Measurement): number => {
    if (isFiniteNumber(measurement.length)) {
        return measurement.length;
    }

    if ((measurement.kind !== 'distance' && measurement.kind !== 'polyline') || measurement.points.length < 2) {
        return 0;
    }

    let total = 0;
    for (let i = 1; i < measurement.points.length; i++) {
        const a = measurement.points[i - 1];
        const b = measurement.points[i];
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        const dz = b[2] - a[2];
        total += Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    return total;
};

const computeArea = (measurement: Measurement): number => {
    if (isFiniteNumber(measurement.area)) {
        return measurement.area;
    }

    return computeAreaFromPoints(measurement);
};

function computeAreaFromPoints(measurement: Measurement): number {
    if (measurement.kind !== 'area' || measurement.points.length < 3) {
        return 0;
    }

    const projected = computeProjectedPolygonArea(measurement.points);
    const areaSquareMeters = projected?.area ?? 0;
    if (measurement.units === 'ft') {
        return areaSquareMeters * SQFT_PER_SQM;
    }
    return areaSquareMeters;
}

const computeAngle = (measurement: Measurement): number => {
    if (measurement.kind !== 'angle' || measurement.points.length !== 3) {
        return 0;
    }

    const a = measurement.points[0];
    const b = measurement.points[1];
    const c = measurement.points[2];

    const abx = a[0] - b[0];
    const aby = a[1] - b[1];
    const abz = a[2] - b[2];
    const cbx = c[0] - b[0];
    const cby = c[1] - b[1];
    const cbz = c[2] - b[2];

    const abLen = Math.sqrt(abx * abx + aby * aby + abz * abz);
    const cbLen = Math.sqrt(cbx * cbx + cby * cby + cbz * cbz);
    if (abLen <= 1e-8 || cbLen <= 1e-8) {
        return 0;
    }

    const dot = abx * cbx + aby * cby + abz * cbz;
    const cosTheta = Math.max(-1, Math.min(1, dot / (abLen * cbLen)));
    return Math.acos(cosTheta) * (180 / Math.PI);
};

const computeValue = (measurement: Measurement): number => {
    if (measurement.kind === 'area') {
        return computeArea(measurement);
    }

    if (measurement.kind === 'angle') {
        return computeAngle(measurement);
    }

    return computeDistance(measurement);
};

const computeUnitsLabel = (measurement: Measurement): string => {
    if (measurement.kind === 'angle') {
        return 'deg';
    }

    if (measurement.kind === 'area') {
        return measurement.units === 'ft' ? 'sq ft' : 'sq m';
    }

    return measurement.units === 'ft' ? 'ft' : 'm';
};

const csvColumns: CsvColumn[] = [
    'id',
    'number',
    'name',
    'type',
    'value',
    'units',
    'description',
    'pointCount',
    'points',
    'createdAt',
    'author',
    'layerId'
];

const escapeCsv = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    const stringValue = String(value);
    if (/[,"\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

const formatCsvPoints = (points: [number, number, number][]): string => {
    return points.map(point => `${point[0]} ${point[1]} ${point[2]}`).join(' | ');
};

const buildMeasurementsCsv = (items: Measurement[]): string => {
    const rows = items.map((item) => {
        const value = computeValue(item);
        const units = computeUnitsLabel(item);

        return [
            item.id,
            item.number ?? '',
            item.name ?? '',
            item.kind,
            Number.isFinite(value) ? value.toFixed(6) : '',
            units,
            item.description ?? '',
            item.points.length,
            formatCsvPoints(item.points),
            item.createdAt ?? '',
            item.author ?? '',
            item.layerId ?? ''
        ];
    });

    const lines = [
        csvColumns.join(','),
        ...rows.map(row => row.map(escapeCsv).join(','))
    ];

    return `${lines.join('\n')}\n`;
};

const removeExtension = (value: string) => {
    return value.replace(/\.[^/.]+$/, '');
};

const sanitizeFilenameSegment = (value: string): string => {
    const sanitized = value.trim().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return sanitized.length > 0 ? sanitized : 'scene';
};

const buildMeasurementsCsvFilename = (docName: string | undefined, now: Date) => {
    const base = sanitizeFilenameSegment(removeExtension(docName ?? 'scene'));
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${base}-measurements-${year}${month}${day}-${hours}${minutes}${seconds}.csv`;
};

const createMeasurementId = (() => {
    let seq = 1;
    return () => `measurement-${Date.now()}-${seq++}`;
})();

const sanitizeMeasurement = (value: unknown): Measurement | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const kind = sanitizeKind(candidate.kind);
    const points = sanitizePoints(candidate.points);

    if (!kind || !points) {
        return null;
    }

    if ((kind === 'area' || kind === 'angle') && points.length < 3) {
        return null;
    }

    if (kind === 'angle' && points.length !== 3) {
        return null;
    }

    const id = typeof candidate.id === 'string' && candidate.id.length > 0 ? candidate.id : createMeasurementId();
    const measurement: Measurement = {
        id,
        kind,
        points
    };

    if (typeof candidate.name === 'string') {
        measurement.name = candidate.name;
    }
    if (typeof candidate.description === 'string') {
        measurement.description = candidate.description;
    }

    const units = sanitizeUnits(candidate.units);
    if (units) {
        measurement.units = units;
    }

    if (typeof candidate.layerId === 'string') {
        measurement.layerId = candidate.layerId;
    }
    if (typeof candidate.author === 'string') {
        measurement.author = candidate.author;
    }
    if (typeof candidate.createdAt === 'string') {
        measurement.createdAt = candidate.createdAt;
    }

    if (isFiniteNumber(candidate.number)) {
        measurement.number = Math.floor(candidate.number);
    }

    if (isFiniteNumber(candidate.length)) {
        measurement.length = candidate.length;
    }

    if (isFiniteNumber(candidate.area)) {
        measurement.area = candidate.area;
    }

    const a = sanitizePoint(candidate.a);
    if (a) {
        measurement.a = a;
    }

    const b = sanitizePoint(candidate.b);
    if (b) {
        measurement.b = b;
    }

    measurement.visible = candidate.visible === undefined ? true : !!candidate.visible;
    measurement.visibilityTouched = !!candidate.visibilityTouched;

    return measurement;
};

const registerMeasurementsEvents = (events: Events) => {
    let measurements: Measurement[] = [];
    let selectedId: string | null = null;
    let nextNumber = 1;
    const maxMeasurementNumber = 999;

    const normalizeNumber = (value: unknown): number | null => {
        if (!isFiniteNumber(value)) {
            return null;
        }

        const normalized = Math.floor(value);
        return normalized >= 1 && normalized <= maxMeasurementNumber ? normalized : null;
    };

    const deriveNextNumber = (items: Measurement[]): number => {
        const used = new Set<number>();
        for (const item of items) {
            const number = normalizeNumber(item.number);
            if (number !== null) {
                used.add(number);
            }
        }

        if (used.size === 0) {
            return 1;
        }

        const highest = Math.max(...used);
        let candidate = highest >= maxMeasurementNumber ? 1 : highest + 1;
        for (let attempts = 0; attempts < maxMeasurementNumber; attempts++) {
            if (!used.has(candidate)) {
                return candidate;
            }
            candidate = candidate >= maxMeasurementNumber ? 1 : candidate + 1;
        }

        return 1;
    };

    const allocateNumber = () => {
        const current = nextNumber;
        nextNumber = current >= maxMeasurementNumber ? 1 : current + 1;
        return current;
    };

    const resolveSelectedNumber = () => {
        if (!selectedId) {
            return null;
        }
        const found = measurements.find(item => item.id === selectedId);
        return found?.number ?? null;
    };

    const listMeasurements = () => measurements.map(cloneMeasurement);

    const fireChanged = () => {
        events.fire('measurements.listChanged', listMeasurements());
        events.fire('measurements.selectedChanged', resolveSelectedNumber());
        events.fire('measure.historyChanged', measurements.map(computeValue));
    };

    const normalizeAddedMeasurement = (value: unknown): Measurement | null => {
        if (value && typeof value === 'object' && 'a' in (value as Record<string, unknown>) && 'b' in (value as Record<string, unknown>)) {
            const candidate = value as LegacyMeasurementAddPayload;
            const a = sanitizePoint(candidate.a);
            const b = sanitizePoint(candidate.b);
            if (!a || !b) {
                return null;
            }

            const measurement: Measurement = {
                id: createMeasurementId(),
                kind: 'distance',
                points: [a, b],
                number: allocateNumber(),
                a,
                b,
                length: isFiniteNumber(candidate.length) ? candidate.length : undefined,
                visible: true,
                visibilityTouched: false
            };

            return measurement;
        }

        const sanitized = sanitizeMeasurement(value);
        if (!sanitized) {
            return null;
        }

        if (!isFiniteNumber(sanitized.number)) {
            sanitized.number = allocateNumber();
        }

        if (sanitized.visible === undefined) {
            sanitized.visible = true;
        }
        if (sanitized.visibilityTouched === undefined) {
            sanitized.visibilityTouched = false;
        }

        if ((!sanitized.a || !sanitized.b) && sanitized.kind === 'distance' && sanitized.points.length >= 2) {
            sanitized.a = sanitized.points[0];
            sanitized.b = sanitized.points[1];
        }

        if (!isFiniteNumber(sanitized.length) && (sanitized.kind === 'distance' || sanitized.kind === 'polyline')) {
            sanitized.length = computeDistance(sanitized);
        }

        if (!isFiniteNumber(sanitized.area) && sanitized.kind === 'area') {
            sanitized.area = computeArea(sanitized);
        }

        return sanitized;
    };

    const setAll = (items: unknown, selected?: string | number | null, next?: number) => {
        let parsed: Measurement[] = [];
        if (Array.isArray(items)) {
            parsed = items.map(sanitizeMeasurement).filter((item): item is Measurement => !!item);
        }

        measurements = parsed;

        if (isFiniteNumber(next)) {
            nextNumber = normalizeNumber(next) ?? deriveNextNumber(measurements);
        } else {
            nextNumber = deriveNextNumber(measurements);
        }

        let selectedNext: string | null = null;
        if (typeof selected === 'string') {
            selectedNext = selected;
        } else if (isFiniteNumber(selected)) {
            selectedNext = measurements.find(item => item.number === selected)?.id ?? null;
        }

        selectedId = selectedNext && measurements.some(item => item.id === selectedNext) ? selectedNext : null;
        fireChanged();
    };

    events.function('measurements.list', () => listMeasurements());
    events.function('measurements.selected', () => selectedId);
    events.function('measurements.selectedNumber', () => resolveSelectedNumber());
    events.function('measurements.nextNumber', () => nextNumber);
    events.function('measurements.csvSchema', () => csvColumns.slice());
    events.function('measurements.exportCsv', () => buildMeasurementsCsv(measurements));
    events.function('measurements.csvFilename', () => {
        const docName = events.invoke('doc.name');
        return buildMeasurementsCsvFilename(typeof docName === 'string' ? docName : undefined, new Date());
    });

    events.on('measurements.select', (idOrNumber: string | number | null) => {
        let next: string | null = null;
        if (typeof idOrNumber === 'string') {
            next = idOrNumber;
        } else if (isFiniteNumber(idOrNumber)) {
            next = measurements.find(item => item.number === idOrNumber)?.id ?? null;
        }

        selectedId = next && measurements.some(item => item.id === next) ? next : null;
        events.fire('measurements.selectedChanged', resolveSelectedNumber());
    });

    events.on('measurements.add', (value: unknown) => {
        const next = normalizeAddedMeasurement(value);
        if (!next) {
            return;
        }
        if (measurements.some(item => item.id === next.id)) {
            return;
        }

        const stamped = withProvenance(events, next);

        if (measurements.length > 0) {
            const previous = measurements[0];
            if (!previous.visibilityTouched) {
                previous.visible = false;
            }
        }

        measurements = [cloneMeasurement(stamped), ...measurements];
        selectedId = stamped.id;
        fireChanged();
    });

    events.on('measurements.update', (payload: MeasurementUpdatePayload) => {
        const id = payload?.id;
        const patch = payload?.patch;

        if (typeof id !== 'string' || !patch || typeof patch !== 'object') {
            return;
        }

        let changed = false;
        measurements = measurements.map((measurement) => {
            if (measurement.id !== id) {
                return measurement;
            }

            const patchRecord = patch as Record<string, unknown>;
            const pointsInPatch = Object.prototype.hasOwnProperty.call(patchRecord, 'points');
            const areaInPatch = Object.prototype.hasOwnProperty.call(patchRecord, 'area');

            const merged = sanitizeMeasurement({
                ...measurement,
                ...patch,
                id: measurement.id
            });

            if (!merged) {
                return measurement;
            }

            if (merged.kind === 'area' && pointsInPatch && !areaInPatch) {
                merged.area = computeAreaFromPoints(merged);
            }

            changed = true;
            return merged;
        });

        if (changed) {
            fireChanged();
        }
    });

    events.on('measurements.remove', (id: string) => {
        if (typeof id !== 'string') {
            return;
        }

        const next = measurements.filter(item => item.id !== id);
        if (next.length === measurements.length) {
            return;
        }

        measurements = next;
        if (selectedId === id) {
            selectedId = measurements[0]?.id ?? null;
        }
        fireChanged();
    });

    // Legacy aliases used by the existing measurement list UI.
    events.on('measurements.delete', (number: number) => {
        if (!isFiniteNumber(number)) {
            return;
        }
        const found = measurements.find(item => item.number === number);
        if (!found) {
            return;
        }
        events.fire('measurements.remove', found.id);
    });

    events.on('measurements.clearAll', () => {
        measurements = [];
        selectedId = null;
        nextNumber = 1;
        fireChanged();
    });

    events.on('measurements.setVisible', (payload: { number: number; visible: boolean }) => {
        const number = payload?.number;
        if (!isFiniteNumber(number)) {
            return;
        }

        let changed = false;
        measurements = measurements.map((measurement) => {
            if (measurement.number !== number) {
                return measurement;
            }

            changed = true;
            return {
                ...measurement,
                visible: !!payload.visible,
                visibilityTouched: true
            };
        });

        if (changed) {
            fireChanged();
        }
    });

    events.on('measurements.applyVisibility', (visibleIds: unknown) => {
        if (!Array.isArray(visibleIds)) {
            return;
        }

        const visibleSet = new Set(visibleIds.filter((id): id is string => typeof id === 'string' && id.length > 0));

        let changed = false;
        measurements = measurements.map((measurement) => {
            const nextVisible = visibleSet.has(measurement.id);
            if (measurement.visible === nextVisible && measurement.visibilityTouched) {
                return measurement;
            }
            changed = true;
            return {
                ...measurement,
                visible: nextVisible,
                visibilityTouched: true
            };
        });

        if (changed) {
            fireChanged();
        }
    });

    events.on('measurements.setAll', (items: unknown) => {
        setAll(items, null, undefined);
    });

    events.on('measurements.restoreState', (payload: MeasurementRestorePayload) => {
        setAll(payload?.items, payload?.selected ?? null, payload?.nextNumber);
    });

    events.on('scene.clear', () => {
        measurements = [];
        selectedId = null;
        nextNumber = 1;
        fireChanged();
    });

    fireChanged();
};

export { registerMeasurementsEvents };
export type { Measurement, MeasurementKind };
